import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { Icon } from './Icon.js';
import JellyfishLoader from './JellyfishLoader';
import ProtocolShield from './ProtocolShield';
import BiohackInfoCard from './BiohackInfoCard';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import './AttachmentSelector.css';
import './ProtocolSelector.css';

const ProtocolSelector = ({ onSelect, onClose, selectedProtocol }) => {
  const [protocols, setProtocols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [selectedBiohack, setSelectedBiohack] = useState(null);
  const contentRef = useRef(null);
  const lastTouchY = useRef(null);

  useEffect(() => {
    fetchProtocols();
    
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Restore body scroll when modal closes
      document.body.style.overflow = '';
    };
  }, []);

  const fetchProtocols = async () => {
    // Deduplicate in-flight requests (e.g. React StrictMode double-mount in dev)
    const cacheKey = getCacheKey('ProtocolSelector_fetchProtocols', 'v1');
    return dedupeRequest(cacheKey, async () => {
      try {
        setLoading(true);
        
        // Get current user (cached, no network request)
        const { data: { session } } = await supabase.auth.getSession();
        const user = session?.user;
        if (!user) {
          setError('You must be signed in to view protocols');
          setProtocols([]);
          setLoading(false);
          return;
        }
        
        // Only fetch protocols created by the current user
        const { data, error } = await supabase
          .from('protocols')
          .select(`
            *,
            adherence_score,
            pop_score,
            protocol_hacks (
              hack_id,
              position,
              biohacks (
                id,
                name,
                image_url,
                category
              )
            ),
            protocol_ratings (
              id,
              rating
            ),
            user_profiles!protocols_created_by_fkey (
              user_id,
              username
            )
          `)
          .eq('created_by', user.id)
          .order('name', { ascending: true });

        if (error) throw error;
        
        // Add created_by_username to each protocol for ProtocolShield display
        const protocolsWithCreator = (data || []).map(protocol => {
          const creatorUsername = protocol.user_profiles?.username || 'Unknown';
          return {
            ...protocol,
            created_by_username: creatorUsername
          };
        });
        
        setProtocols(protocolsWithCreator);
      } catch (err) {
        console.error('Error fetching protocols:', err);
        setError('Failed to load protocols');
      } finally {
        setLoading(false);
      }
    });
  };

  const filteredProtocols = protocols.filter(protocol =>
    protocol.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    protocol.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleProtocolClick = (protocol) => {
    // If clicking on the already selected protocol, deselect it
    if (selectedProtocol && selectedProtocol.id === protocol.id) {
      onSelect(null);
    } else {
      onSelect(protocol);
    }
  };

  const handleDone = () => {
    onClose();
  };

  if (loading) {
    return (
      <div className="attachment-selector-overlay" onClick={onClose}>
        <div className="attachment-selector" onClick={(e) => e.stopPropagation()}>
          <div className="attachment-selector-loading">
            <JellyfishLoader />
          </div>
        </div>
      </div>
    );
  }

  const handleOverlayClick = (e) => {
    // Only close if clicking directly on the overlay, not on the modal content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleOverlayWheel = (e) => {
    // Only prevent if the event is directly on the overlay (not bubbling from content)
    // The content handler will handle its own events
    if (e.target === e.currentTarget) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleContentWheel = (e) => {
    const content = contentRef.current;
    if (!content) return;

    const { scrollTop, scrollHeight, clientHeight } = content;
    const isAtTop = scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1; // Allow 1px tolerance
    
    // If scrolling up at the top, or scrolling down at the bottom, prevent default
    if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
      e.preventDefault();
      e.stopPropagation();
    } else {
      // Normal scrolling - just prevent bubbling
      e.stopPropagation();
    }
  };

  const handleContentTouchStart = (e) => {
    if (e.touches.length > 0) {
      lastTouchY.current = e.touches[0].clientY;
    }
    e.stopPropagation();
  };

  const handleContentTouchMove = (e) => {
    const content = contentRef.current;
    if (!content) {
      e.stopPropagation();
      return;
    }

    const { scrollTop, scrollHeight, clientHeight } = content;
    const isAtTop = scrollTop === 0;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
    
    if (e.touches.length > 0 && lastTouchY.current !== null) {
      const currentTouchY = e.touches[0].clientY;
      const deltaY = currentTouchY - lastTouchY.current;
      lastTouchY.current = currentTouchY;
      
      // If trying to scroll up at top or down at bottom, prevent default
      if ((deltaY > 0 && isAtTop) || (deltaY < 0 && isAtBottom)) {
        e.preventDefault();
        e.stopPropagation();
      } else {
        e.stopPropagation();
      }
    } else {
      e.stopPropagation();
    }
  };

  return (
    <div 
      className="attachment-selector-overlay" 
      onClick={handleOverlayClick}
      onWheel={handleOverlayWheel}
      onTouchMove={(e) => {
        // Only prevent if not touching the modal content
        if (e.target === e.currentTarget) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <div className="attachment-selector" onClick={(e) => e.stopPropagation()}>
        <div className="attachment-selector-header">
          <div className="attachment-selector-title">
            <Icon name="TrendingUp" className="icon--sm" />
            <span>Select Protocol</span>
          </div>
          <div className="attachment-selector-actions">
            <button 
              className="attachment-search-toggle"
              onClick={() => setShowSearch(!showSearch)}
            >
              <Icon name="Search" className="icon--sm" />
            </button>
            <button className="attachment-selector-close" onClick={onClose}>
              <Icon name="X" className="icon--sm" />
            </button>
          </div>
        </div>

        {showSearch && (
          <div className="attachment-selector-search">
            <input
              type="text"
              placeholder="Search protocols..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="attachment-search-input"
              autoFocus
            />
            <button 
              className="attachment-search-close"
              onClick={() => {
                setShowSearch(false);
                setSearchTerm('');
              }}
            >
              <Icon name="X" className="icon--xs" />
            </button>
          </div>
        )}

        <div 
          ref={contentRef}
          className="attachment-selector-content" 
          onWheel={handleContentWheel} 
          onTouchStart={handleContentTouchStart}
          onTouchMove={handleContentTouchMove}
        >
          {error ? (
            <div className="attachment-selector-error">
              <Icon name="AlertCircle" className="icon--sm" />
              <span>{error}</span>
            </div>
          ) : filteredProtocols.length === 0 ? (
            <div className="attachment-selector-empty">
              <Icon name="TrendingUp" className="icon--md" />
              <span>No protocols found</span>
            </div>
          ) : (
            <div className="protocol-selector-shields-grid">
              {filteredProtocols.map(protocol => (
                <div
                  key={protocol.id}
                  className={`protocol-selector-shield-item ${selectedProtocol?.id === protocol.id ? 'selected' : ''}`}
                  onClick={() => handleProtocolClick(protocol)}
                >
                  <ProtocolShield
                    protocol={protocol}
                    size="medium"
                    onBiohackClick={setSelectedBiohack}
                    disableFlip={true}
                    hideFabMenu={true}
                  />
                  {selectedProtocol?.id === protocol.id && (
                    <div className="protocol-selector-check-overlay">
                      <Icon name="Check" className="icon--md" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="attachment-selector-footer">
          <div className="attachment-selection-info">
            {selectedProtocol ? `Selected: ${selectedProtocol.name}` : 'No protocol selected'}
          </div>
          <button
            className="attachment-done-btn"
            onClick={handleDone}
          >
            Done
          </button>
        </div>
      </div>

      {/* Biohack Info Card Modal */}
      {selectedBiohack && (
        <BiohackInfoCard
          biohack={selectedBiohack}
          onClose={() => setSelectedBiohack(null)}
          onRatingSubmitted={() => {
            // Refresh data if needed
            console.log('Rating submitted for biohack from protocol selector');
          }}
        />
      )}
    </div>
  );
};

export default ProtocolSelector;
